import React from 'react';
import { motion } from 'framer-motion';
import { useGame } from '../context/GameContext';
import { Play, Trophy } from 'lucide-react';

const StartScreen = ({ onShowLeaderboard }) => {
  const { startGame } = useGame();

  return (
    <motion.div 
      className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Logo/Title */}
      <motion.div
        className="text-center mb-8"
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        <h1 className="font-fredoka text-4xl sm:text-5xl lg:text-6xl text-[#293241] mb-4">
          Do you know
          <br />
          <span className="text-[#EE6C4D]">your emojis?</span>
        </h1>
        <p className="font-quicksand text-base sm:text-lg text-[#293241]/70 max-w-md mx-auto">
          Find the emoji that doesn't belong!
          <br />
          One mistake and it's game over!
        </p>
      </motion.div>

      {/* Animated emoji showcase */}
      <motion.div 
        className="flex flex-wrap justify-center gap-3 mb-10 max-w-md"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.5 }}
      >
        {['🐕', '🍕', '⚽', '🚗', '🎸', '😀', '❤️', '🌈'].map((emoji, i) => (
          <motion.div
            key={emoji}
            className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-white shadow-lg flex items-center justify-center text-2xl sm:text-3xl"
            animate={{ y: [0, -8, 0] }}
            transition={{ 
              duration: 2,
              delay: i * 0.15,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            {emoji}
          </motion.div>
        ))}
      </motion.div>

      {/* Play Button */}
      <motion.div
        className="w-full max-w-xs mb-4"
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.5 }}
      >
        <button
          data-testid="start-button"
          className="btn-primary flex items-center justify-center gap-3 w-full text-xl"
          onClick={() => startGame('emoji')}
        >
          <Play size={24} />
          Play Now
        </button>
      </motion.div>

      {/* Leaderboard Button */}
      <motion.div
        className="w-full max-w-xs"
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.7, duration: 0.5 }}
      >
        <button
          data-testid="leaderboard-button"
          className="btn-secondary flex items-center justify-center gap-3 w-full"
          onClick={onShowLeaderboard}
        >
          <Trophy size={20} />
          Leaderboard
        </button>
      </motion.div>

      {/* Instructions */}
      <motion.div
        className="mt-10 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 0.5 }}
      >
        <div className="flex flex-col sm:flex-row gap-6 sm:gap-10 text-sm text-[#293241]/60">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-[#FFD23F] flex items-center justify-center text-sm font-bold">1</span>
            <span>See the emojis</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-[#3D5A80] text-white flex items-center justify-center text-sm font-bold">2</span>
            <span>Find the odd one</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-[#06D6A0] flex items-center justify-center text-sm font-bold">3</span>
            <span>Be fast!</span>
          </div>
        </div>
      </motion.div>

      {/* Category hint */}
      <motion.p
        className="mt-8 text-xs text-[#293241]/40 font-quicksand"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.5 }}
      >
        40+ categories • 800+ emojis
      </motion.p>
    </motion.div>
  );
};

export default StartScreen;
